//
// # Stretch Wire Endpoint Mode Handler
//
// Allows users to drag a wire segment endpoint to extend/retract the wire.
// The endpoint is constrained to Manhattan directions (horizontal/vertical)
// and snaps to nearby pins for easy connectivity.
//

import { Point, graphics, model } from "GenAlphaModel";
import * as changes from "DrawCore/src/changes";
import { ModeHandler } from "./base";
import { DragThreshold } from "./axis_constrain";
import { DrawTo } from "DrawCore/src/draw";
import * as style from "DrawCore/src/style";
import {
  buildPinLocationCache,
  snapToNearbyPin,
  type PinLocationCache,
} from "DrawCore/src/routing";
import type { InteractionHost } from "../interaction_host";
import type { WireSegment } from "DrawCore/src/content/schematic/wire";
import type { Schematic } from "../content/schematic/schematic";

// Radius of the visual indicator circle for the stretched endpoint
const STRETCH_INDICATOR_RADIUS = 8;

// Style for the stretch indicator circle
const stretchIndicatorStyle: style.ShapeStyle = {
  cap: "round",
  join: "round",
  // Semi-transparent orange fill to indicate active stretching
  fill: style.Color.new(255, 165, 0, 0.4),
  stroke: style.Color.new(255, 140, 0, 1.0),
  linewidth: 2,
};

// How the stretch was initiated. Drag-initiated modes commit on mouse-up
// (the user is holding LMB); keyboard-initiated modes commit on the next
// LMB click and cancel on RMB, matching the MoveSelection (G key) UX.
type StretchTrigger = "drag" | "keyboard";

export class StretchWireEndpoint extends ModeHandler {
  override movesGeometryLive = true;

  // Cache of pin locations for magnetic snapping
  private readonly pinCache: PinLocationCache;
  // Original endpoints for undo/abort
  private readonly origEndpoints: [Point, Point];
  // Original labels, deep-cloned, for change-log `from` data and abort/restore.
  private readonly origLabels: Array<graphics.Text>;
  // Suppresses movement until the cursor leaves a small pixel radius around the
  // press. A mouse-drag arms it; the keyboard stretch tracks the cursor immediately.
  private readonly dragThreshold: DragThreshold;

  private constructor(
    private readonly editor: InteractionHost,
    private readonly schematic: Schematic,
    private readonly wire: WireSegment,
    private readonly endpointIndex: 0 | 1,
    private readonly trigger: StretchTrigger
  ) {
    super();
    // Store original endpoints for undo/abort
    this.origEndpoints = [Point.clone(wire.start()), Point.clone(wire.end())];
    this.origLabels = model.schematic.cloneWireLabels(wire.data.labels);
    // Build pin cache for magnetic snapping
    this.pinCache = buildPinLocationCache(schematic.render);
    this.dragThreshold =
      trigger === "drag"
        ? new DragThreshold(Point.clone(editor.uiState.mousePos.page))
        : DragThreshold.passthrough();
    editor.overlays.setStatusMessage(
      trigger === "keyboard"
        ? "Stretch: left-click to confirm, right-click to cancel"
        : "Stretch: drag to extend wire",
      "stretch-wire"
    );
  }

  // Enter stretch mode for the given wire endpoint
  static enter(
    schematic: Schematic,
    wire: WireSegment,
    endpointIndex: 0 | 1
  ): unknown {
    const editor = schematic.editor;
    const mode = new StretchWireEndpoint(
      editor,
      schematic,
      wire,
      endpointIndex,
      "drag"
    );
    schematic.select(wire);
    return editor.setMode(mode);
  }

  // Keyboard-initiated entry (G key on a hovered endpoint). Drives the same
  // logic as `enter`, but commit/cancel are LMB/RMB instead of mouseup.
  static enterKeyboard(
    schematic: Schematic,
    wire: WireSegment,
    endpointIndex: 0 | 1
  ): unknown {
    const editor = schematic.editor;
    const mode = new StretchWireEndpoint(
      editor,
      schematic,
      wire,
      endpointIndex,
      "keyboard"
    );
    schematic.select(wire);
    return editor.setMode(mode);
  }

  // Apply magnetic snap to a point if within range of a pin
  private applyPinSnap(point: Point): Point {
    return snapToNearbyPin(point, this.pinCache) ?? point;
  }

  // Draw a visual indicator at the endpoint being stretched
  override draw = (to: DrawTo): void => {
    const { wire, endpointIndex } = this;
    const scale = this.editor.canvas.worldTransforms.schToPage.scale;

    // Get the current position of the endpoint being stretched
    const endpoint = endpointIndex === 0 ? wire.start() : wire.end();

    // Draw a circle indicator at the stretched endpoint
    // Scale the radius inverse to zoom so it appears constant size on screen
    const scaledRadius = STRETCH_INDICATOR_RADIUS / scale;
    const diameter = scaledRadius * 2;
    const ellipse = graphics.Ellipse.fromPartial({
      filled: true,
      bbox: model.Bbox.fromSizeAndCenter(
        { width: diameter, height: diameter },
        endpoint
      ),
    });

    to.ellipse(ellipse, {
      ...stretchIndicatorStyle,
      linewidth: stretchIndicatorStyle.linewidth / scale,
    });
  };

  // Update the wire endpoint position on mouse move
  override handleMouseMove = () => {
    const { editor, wire, endpointIndex, origEndpoints, origLabels } = this;
    const { mousePos } = editor.uiState;

    // Ignore sub-threshold cursor motion so a plain click doesn't stretch the wire.
    if (!this.dragThreshold.begun(mousePos.page)) return;

    // Get the fixed endpoint (the one NOT being dragged)
    const fixedIndex = endpointIndex === 0 ? 1 : 0;
    const fixedPoint = origEndpoints[fixedIndex];

    // Snap cursor to grid with Manhattan constraint relative to fixed endpoint
    const gridPoint = editor.grid.nearestManhattan(mousePos.sch, fixedPoint);

    // Apply magnetic pin snap
    const newPoint = this.applyPinSnap(gridPoint);

    // Update wire endpoints; labels re-center on the resulting geometry so
    // they follow the stretched wire.
    const endpoints: [Point, Point] =
      endpointIndex === 0 ? [newPoint, fixedPoint] : [fixedPoint, newPoint];
    wire.update({
      endpoints,
      labels: model.schematic.recenterLabels(
        origLabels,
        endpoints[0],
        endpoints[1]
      ),
    });

    // Request redraw to update the visual indicator
    return editor.requestRedraw();
  };

  // Drag-initiated: commit on mouse up.
  // Keyboard-initiated: ignore mouseup (commit comes from the next LMB down).
  override handleMouseUp = () => {
    if (this.trigger === "drag") {
      this.commit();
    }
  };

  // Keyboard-initiated only: LMB confirms, any other button cancels.
  // For drag-initiated stretches the mouse button is already held; no extra
  // mousedown is meaningful there.
  override handleMouseDown = (e: MouseEvent) => {
    if (this.trigger !== "keyboard") return;
    if (e.button === 0) {
      this.commit();
    } else {
      this.abort();
      this.editor.requestRedraw();
    }
  };

  // Handle keystrokes
  override handleKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      this.abort();
      return;
    }
    if (e.key === "Enter") {
      this.commit();
      return;
    }
  };

  // Commit the stretch operation
  private commit = () => {
    const { editor, schematic, wire, origEndpoints } = this;

    const currentStart = wire.start();
    const currentEnd = wire.end();

    // Check if wire has collapsed to zero length
    if (Point.eq(currentStart, currentEnd)) {
      // Delete the wire segment
      schematic.removeEntity(wire, true);
      schematic.logChange(changes.remove(wire));
      schematic.updateDots();
      editor.overlays.clearStatusMessage("stretch-wire");
      editor.deselect();
      editor.goUiIdle();
      return;
    }

    // Log the change if endpoints actually changed
    const didChange =
      !Point.eq(origEndpoints[0], currentStart) ||
      !Point.eq(origEndpoints[1], currentEnd);

    if (didChange) {
      schematic.logChange(
        changes.update({
          target: wire,
          from: { endpoints: origEndpoints, labels: this.origLabels },
          to: {
            endpoints: [currentStart, currentEnd],
            labels: model.schematic.recenterLabels(
              this.origLabels,
              currentStart,
              currentEnd
            ),
          },
        })
      );
      schematic.updateDots();
    }

    editor.overlays.clearStatusMessage("stretch-wire");
    editor.deselect();
    editor.goUiIdle();
  };

  // Abort and restore original positions
  override abort = () => {
    const { editor, schematic, wire, origEndpoints, origLabels } = this;

    // Restore original endpoints and labels
    wire.update({
      endpoints: origEndpoints,
      labels: model.schematic.cloneWireLabels(origLabels),
    });
    schematic.updateDots();

    editor.overlays.clearStatusMessage("stretch-wire");
    editor.deselect();
    editor.goUiIdle();
  };
}
